import React, { useState, useContext, useCallback, ChangeEvent } from "react";
import { Box, Typography, Button, Stack, LinearProgress } from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useTranslation } from "react-i18next";
import useAlerts from "./useImportAlerts";
import TraxeroAlert from "components/importer/ImportAlert";
import { DataWidget, WIZARD_ACTION } from "types";
import AuturaDropdown from "components/inputs/AuturaDropdown";
import { ImportWizardContext } from "components/importer/ImportWizardContext";

dayjs.extend(customParseFormat);
interface ExtractVehicleWidgetProps {
  setDataWidget: (value: DataWidget) => void;
  setSelectedOption: (value: string) => void;
}

export const ExtractVehicleWidget: React.FC<ExtractVehicleWidgetProps> = ({
  setDataWidget,
  setSelectedOption,
}) => {
  const { t } = useTranslation();

  const { state, dispatch, parseVehicleData } = useContext(ImportWizardContext);
  const ALERTS = useAlerts({ entityName: state.entityName });
  const [isProcessing, setIsProcessing] = useState(false);

  const handleParseVehicleData = useCallback(() => {
    if (!state.selectedCsvField) {
      return;
    }
    setIsProcessing(true);
    parseVehicleData(state.selectedCsvField);
    setIsProcessing(false);
    dispatch({
      type: WIZARD_ACTION.SET_SELECTED_CSV_FIELD,
      payload: null,
    });
    setDataWidget("preview");
  }, [state.selectedCsvField, dispatch, parseVehicleData]);

  const handleCancel = () => {
    setSelectedOption("");
    setDataWidget("none");
  };

  return (
    <Stack direction="column" spacing={4}>
      <TraxeroAlert {...ALERTS.EXTRACT_VEHICLE_INSTRUCTIONS} />
      {isProcessing && (
        <Box width="100%">
          <Typography variant="h6" width="100%" textAlign="center">
            {t("processingVehicleData", { ns: "importWizard" })}
          </Typography>
          <LinearProgress variant="indeterminate" />
        </Box>
      )}
      <Stack direction="row" width="100%" display="flex">
        <Box sx={{ flex: 1 }}>
          <AuturaDropdown
            name="csvField"
            options={state.csvHeaders.filter(
              (header) => !header.startsWith("_"),
            )}
            value={state.selectedCsvField || ""}
            label={t("csvField", { ns: "importWizard" })}
            setValue={(e: ChangeEvent<HTMLInputElement>) => {
              dispatch({
                type: WIZARD_ACTION.SET_SELECTED_CSV_FIELD,
                payload: e.target.value,
              });
            }}
            sx={{ width: "100%" }}
            menuItemProps={{
              divider: true,
            }}
          />
        </Box>
      </Stack>
      <Stack direction="row" display="flex" justifyContent="space-between">
        <Button
          onClick={handleCancel}
          variant="text"
          color="error"
          disabled={isProcessing}
        >
          {t("cancel")}
        </Button>
        <Button
          onClick={handleParseVehicleData}
          variant="text"
          color="primary"
          disabled={isProcessing || !state.selectedCsvField}
        >
          {t("buttons.map_vehicle_data", { ns: "importWizard" })}
        </Button>
      </Stack>
    </Stack>
  );
};
